/**
 * 邀请功能 API 服务
 */

export interface InviteStats {
  registrationEnabled: boolean;
  purchaseEnabled: boolean;
  inviteCode: string;
  rewardCount: number;
  totalInviteCount: number;
  rewardLimit: number;
  remainingRewards: number;
  reachedRewardLimit: boolean;
}

export interface InviteFeatures {
  registrationEnabled: boolean;
  purchaseEnabled: boolean;
}

export async function getInviteFeatures(): Promise<InviteFeatures> {
  const response = await fetch('/invite/config');
  const result = await response.json();
  if (!result.isSuccess || !result.data) throw new Error('Failed to load invitation configuration');
  return {
    registrationEnabled: result.data.registrationEnabled === true,
    purchaseEnabled: result.data.purchaseEnabled === true,
  };
}

export interface InviteRecord {
  id: number;
  inviteCode: string;
  inviteeUsername: string;
  inviteeFirstLoginTime: number;
  rewardGiven: boolean;
  rewardGivenTime: number;
  createTime: number;
}

/**
 * 获取邀请码
 */
export async function getInviteCode(): Promise<string> {
  const response = await fetch('/invite/code', {
    method: 'GET',
    credentials: 'include',
  });
  const result = await response.json();
  if (result.isSuccess && result.data) {
    return result.data.inviteCode;
  }
  throw new Error(result.message || '获取邀请码失败');
}

/**
 * 获取邀请统计信息
 */
export async function getInviteStats(): Promise<InviteStats> {
  const response = await fetch('/invite/stats', {
    method: 'GET',
    credentials: 'include',
  });
  const result = await response.json();
  if (result.isSuccess && result.data) {
    return result.data;
  }
  throw new Error(result.message || '获取统计信息失败');
}

/**
 * 获取邀请记录列表
 */
export async function getInviteRecords(): Promise<InviteRecord[]> {
  const response = await fetch('/invite/records', {
    method: 'GET',
    credentials: 'include',
  });
  const result = await response.json();
  if (result.isSuccess && result.data) {
    return result.data;
  }
  throw new Error(result.message || '获取邀请记录失败');
}

/**
 * 使用邀请码（注册时调用，可选）
 */
export async function useInviteCode(inviteCode: string, inviteeUsername: string): Promise<void> {
  const response = await fetch('/invite/use', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json;charset=utf8',
    },
    credentials: 'include',
    body: JSON.stringify({
      inviteCode,
      inviteeUsername,
    }),
  });
  const result = await response.json();
  if (!result.isSuccess) {
    throw new Error(result.message || '使用邀请码失败');
  }
}

export interface CommissionSummary {
  availableBalanceCents: number;
  lifetimeEarnedCents: number;
  paidOutCents: number;
  negativeBalanceCents: number;
}

export async function getCommissionSummary(): Promise<CommissionSummary> {
  const response = await fetch('/invite/commission/summary', { credentials: 'include' });
  const result = await response.json();
  if (result.isSuccess && result.data) return result.data;
  throw new Error(result.message || '获取返佣余额失败');
}

export async function getCommissionRecords(page = 1, pageSize = 20): Promise<{ data: Record<string, unknown>[]; total: number }> {
  const response = await fetch(`/invite/commission/records?page=${page}&pageSize=${pageSize}`, { credentials: 'include' });
  const result = await response.json();
  if (result.isSuccess && Array.isArray(result.data)) return { data: result.data, total: result.total || 0 };
  throw new Error(result.message || '获取返佣记录失败');
}
